import { useRef, useState, type DragEvent } from "react";
import { useQuery } from "@tanstack/react-query";
import { ServerTile } from "@entities/server";
import { Dialog, Field, Hint } from "@shared/ui/bits";
import { Button } from "@shared/ui/button";
import { Input } from "@shared/ui/input";
import { useStore } from "@features/chat";
import { api, keys, queryClient, type RailEntry, type Uuid } from "@shared/api";
import { IconFolder } from "@shared/ui/icons";
import { Mark } from "@shared/ui/Brand";
import { tally } from "@shared/lib/unread";
import { useSlide } from "@shared/motion";
import { MenuOn, SEPARATOR, type MenuItem } from "@shared/ui/Menu";
import type { ServerSummary } from "@shared/api";
import {
  arrange,
  editFolder,
  moveFolder,
  moveServer,
  newFolder,
  takeOut,
  ungroup,
  type Drop,
  type Folder,
  type RailItem,
} from "../lib/layout";

/** Цвета папок — как в Discord, восемь тонов. */
const FOLDER_COLORS = ["#5865f2", "#3ba55c", "#faa61a", "#ed4245", "#eb459e", "#9b59b6", "#1abc9c", "#99aab5"];

const OPEN_KEY = "airel.rail-open";

function readOpen(): Set<Uuid> {
  try {
    return new Set(JSON.parse(localStorage.getItem(OPEN_KEY) ?? "[]") as Uuid[]);
  } catch {
    return new Set();
  }
}

function toItem(entry: RailEntry): RailItem {
  return entry.kind === "folder"
    ? { kind: "folder", id: entry.id, name: entry.name ?? null, color: entry.color ?? null, servers: entry.servers ?? [] }
    : { kind: "server", id: entry.id };
}

/** Где на месте опустили: верх и низ — встать рядом, середина — в папку. */
function zone(event: DragEvent<HTMLElement>, into: boolean): Drop["how"] {
  const box = event.currentTarget.getBoundingClientRect();
  const at = (event.clientY - box.top) / box.height;
  if (into && at > 0.25 && at < 0.75) return "into";
  return at < 0.5 ? "before" : "after";
}

export type Place = { kind: "server"; id: Uuid } | { kind: "directs" } | { kind: "friends" };

/**
 * Ряд серверов. Личные переписки — отдельный режим в самом верху: они
 * переключают всю левую часть, а не притворяются ещё одним сервером.
 *
 * Место под счётчик упоминаний занято всегда — их пока не считают, и когда
 * счётчик появится, ряд не переедет.
 */
export function Rail({
  place,
  onGo,
  onCreate,
  menuFor,
}: {
  place: Place;
  onGo: (place: Place) => void;
  onCreate: () => void;
  /** Меню сервера по правой кнопке — как в Discord. */
  menuFor: (server: ServerSummary) => MenuItem[];
}) {
  const { servers, serverUnread, serverMentions } = useStore();
  const { data: saved } = useQuery({
    queryKey: keys.rail(),
    queryFn: () => api.users.rail().then((page) => page.items.map(toItem)),
  });
  const layout = arrange(saved ?? [], servers.map((server) => server.id));
  const byId = new Map(servers.map((server) => [server.id, server]));
  const [opened, setOpened] = useState(readOpen);
  const [dragged, setDragged] = useState<{ kind: "server" | "folder"; id: Uuid } | null>(null);
  const [hover, setHover] = useState<Drop | null>(null);
  const [renaming, setRenaming] = useState<Folder | null>(null);
  const activeId = place.kind === "server" ? place.id : null;
  const list = useRef<HTMLDivElement>(null);
  /*
   * Засечка выбранного одна на весь ряд и едет к новому выбору, а не
   * зажигается на нём — так в макете v6. Засечки «есть новое» остаются у
   * каждого значка своими.
   */
  const bar = useSlide(list, ".rail-item.active", place.kind === "server" ? place.id : "directs");

  /** Раскладку — сразу на экран и на сервер; отказ — перечитать сохранённую. */
  function save(next: RailItem[]) {
    queryClient.setQueryData(keys.rail(), next);
    void api.users
      .saveRail(
        next.map((item) =>
          item.kind === "server"
            ? { kind: "server", id: item.id }
            : { kind: "folder", id: item.id, name: item.name, color: item.color, servers: item.servers },
        ),
      )
      .catch(() => queryClient.invalidateQueries({ queryKey: keys.rail() }));
  }

  function toggle(folderId: Uuid) {
    setOpened((was) => {
      const next = new Set(was);
      if (next.has(folderId)) next.delete(folderId);
      else next.add(folderId);
      try {
        localStorage.setItem(OPEN_KEY, JSON.stringify([...next]));
      } catch {
        // Без памяти раскрытость просто не переживёт перезагрузку.
      }
      return next;
    });
  }

  /** Перетаскивание — как в Discord: сервер на сервер — папка, на край — встать рядом. */
  function dragProps(kind: "server" | "folder", id: Uuid, into: boolean) {
    return {
      draggable: true,
      onDragStart: (event: DragEvent<HTMLElement>) => {
        event.dataTransfer.effectAllowed = "move";
        event.dataTransfer.setData("text/plain", id);
        setDragged({ kind, id });
      },
      onDragOver: (event: DragEvent<HTMLElement>) => {
        if (!dragged || dragged.id === id) return;
        event.preventDefault();
        const how = zone(event, into && dragged.kind === "server");
        if (hover?.at !== id || hover.how !== how) setHover({ how, at: id });
      },
      onDragLeave: () => setHover((was) => (was?.at === id ? null : was)),
      onDrop: (event: DragEvent<HTMLElement>) => {
        event.preventDefault();
        if (dragged && hover) {
          save(
            dragged.kind === "server"
              ? moveServer(layout, dragged.id, hover, () => crypto.randomUUID())
              : moveFolder(layout, dragged.id, hover),
          );
        }
        setDragged(null);
        setHover(null);
      },
      onDragEnd: () => {
        setDragged(null);
        setHover(null);
      },
    };
  }

  function dropClass(id: Uuid): string {
    return hover?.at === id ? `rail-drop-${hover.how}` : "";
  }

  function serverButton(server: ServerSummary, folder: Folder | null) {
    const extra: MenuItem[] = folder
      ? [{ label: "Убрать из папки", onPick: () => save(takeOut(layout, server.id)) }]
      : [{ label: "Новая папка", onPick: () => save(newFolder(layout, server.id, () => crypto.randomUUID())) }];

    return (
      <Hint key={server.id} label={server.name} side="right">
        <MenuOn title={server.name} items={[...menuFor(server), SEPARATOR, ...extra]}>
        <button
          type="button"
          aria-label={
            (serverUnread[server.id] ?? 0) > 0
              ? `${server.name}, непрочитанных ${tally(serverUnread[server.id], serverMentions[server.id] ?? 0)}`
              : server.name
          }
          className={`rail-item ${activeId === server.id ? "active" : ""} ${dropClass(server.id)}`}
          onClick={() => onGo({ kind: "server", id: server.id })}
          {...dragProps("server", server.id, true)}
        >
          {/* Засечка одна: у открытого сервера она длинная и акцентом, у
              сервера с непрочитанным — короткая и светлая. */}
          {(activeId === server.id || (serverUnread[server.id] ?? 0) > 0) && <span className="rail-notch" />}
          <span className="rail-tile">
            <ServerTile name={server.name} id={server.id} iconId={server.icon_id} size={44} />
            {(serverUnread[server.id] ?? 0) > 0 && (
              <span className={`rail-badge ${serverMentions[server.id] ? "" : "quiet"}`}>
                {tally(serverUnread[server.id], serverMentions[server.id] ?? 0)}
              </span>
            )}
          </span>
        </button>
        </MenuOn>
      </Hint>
    );
  }

  /**
   * Папка — как в Discord: свёрнутая — сетка значков 2×2 на её цвете и общий
   * счётчик, развёрнутая — значок папки и её серверы на цветной подложке.
   */
  function folderBlock(folder: Folder) {
    const open = opened.has(folder.id);
    const members = folder.servers.map((id) => byId.get(id)).filter((server): server is ServerSummary => !!server);
    const name = folder.name ?? members.map((server) => server.name).join(", ");
    const unread = folder.servers.reduce((sum, id) => sum + (serverUnread[id] ?? 0), 0);
    const named = folder.servers.reduce((sum, id) => sum + (serverMentions[id] ?? 0), 0);
    const holdsActive = !!activeId && folder.servers.includes(activeId);
    const items: MenuItem[] = [
      { label: open ? "Свернуть папку" : "Развернуть папку", onPick: () => toggle(folder.id) },
      { label: "Переименовать", onPick: () => setRenaming(folder) },
      {
        label: "Цвет",
        items: FOLDER_COLORS.map((color) => ({
          label: color,
          choice: true,
          on: (folder.color ?? FOLDER_COLORS[0]) === color,
          onPick: () => save(editFolder(layout, folder.id, { color })),
        })),
      },
      SEPARATOR,
      { label: "Распустить папку", kind: "danger", onPick: () => save(ungroup(layout, folder.id)) },
    ];

    return (
      <div
        key={folder.id}
        className={`rail-folder ${open ? "open" : ""}`}
        style={{ "--folder": folder.color ?? FOLDER_COLORS[0] } as React.CSSProperties}
      >
        <Hint label={name} side="right">
          <MenuOn title={`ПАПКА · ${name.toUpperCase()}`} items={items}>
          <button
            type="button"
            aria-label={`Папка ${name}${unread > 0 ? `, непрочитанных ${tally(unread, named)}` : ""}`}
            aria-expanded={open}
            className={`rail-item ${!open && holdsActive ? "active" : ""} ${dropClass(folder.id)}`}
            onClick={() => toggle(folder.id)}
            {...dragProps("folder", folder.id, true)}
          >
            {((!open && holdsActive) || (!open && unread > 0)) && <span className="rail-notch" />}
            <span className="rail-tile">
              {open ? (
                <span className="rail-folder-icon">
                  <IconFolder size={20} />
                </span>
              ) : (
                <span className="rail-folder-grid">
                  {members.slice(0, 4).map((server) => (
                    <ServerTile key={server.id} name={server.name} id={server.id} iconId={server.icon_id} size={16} />
                  ))}
                </span>
              )}
              {!open && unread > 0 && (
                <span className={`rail-badge ${named ? "" : "quiet"}`}>{tally(unread, named)}</span>
              )}
            </span>
          </button>
          </MenuOn>
        </Hint>
        {open && members.map((server) => serverButton(server, folder))}
      </div>
    );
  }

  return (
    <nav className="rail glass-host" aria-label="Серверы">
      {/*
        Прокручивается не сам ряд, а список внутри: стекло лежит в ряду, и
        прокрутка увозила бы его вместе со значками.
      */}
      <div className="rail-list" ref={list}>
      {bar && (
        <span
          className="rail-bar"
          style={{ top: bar.top + bar.height / 2 - 14, transition: bar.moved ? undefined : "none" }}
        />
      )}
      {/*
        Подсказки сбоку, а не сверху: ряд узкий и прижат к левому краю, и
        подсказка над значком накрывала бы соседа выше.
      */}
      {/*
        Имя кнопкам задано отдельно от подсказки, и это не дубль.

        Подсказка Radix подставляет своё описание только пока открыта — то
        есть пока на значок навели курсор. Тому, кто читает экран голосом,
        она не достаётся никогда, а внутри у этих кнопок либо один значок,
        либо две буквы из имени сервера и счётчик. Так «Airel Общий»
        читалось как «АО 99+», а ряд переписок — вообще безымянной кнопкой.
      */}
      <Hint label="Личные переписки" side="right">
        <button
          type="button"
          aria-label="Личные переписки"
          className={`rail-item ${place.kind !== "server" ? "active" : ""}`}
          onClick={() => onGo({ kind: "directs" })}
        >
          {place.kind !== "server" && <span className="rail-notch" />}
          {/* Знак Airel, как у Discord его знак: дом — личные переписки. */}
          <span className="rail-dm rail-home">
            <Mark size="bar" />
          </span>
        </button>
      </Hint>

      <span className="rail-sep" />

      {layout.map((item) => {
        if (item.kind === "server") {
          const server = byId.get(item.id);
          return server ? serverButton(server, null) : null;
        }
        return folderBlock(item);
      })}

      <Hint label="Создать сервер или войти по коду" side="right">
        <button
          type="button"
          aria-label="Создать сервер или войти по коду"
          className="rail-add"
          onClick={onCreate}
        >
          +
        </button>
      </Hint>
      </div>
      {renaming && (
        <RenameFolder
          folder={renaming}
          onSave={(name) => save(editFolder(layout, renaming.id, { name }))}
          onClose={() => setRenaming(null)}
        />
      )}
    </nav>
  );
}

function RenameFolder({
  folder,
  onSave,
  onClose,
}: {
  folder: Folder;
  onSave: (name: string | null) => void;
  onClose: () => void;
}) {
  const [name, setName] = useState(folder.name ?? "");
  const done = () => {
    onSave(name.trim() || null);
    onClose();
  };

  return (
    <Dialog
      title="Название папки"
      onClose={onClose}
      actions={
        <>
          <Button type="button" variant="secondary" onClick={onClose}>
            Отмена
          </Button>
          <Button type="button" onClick={done}>
            Сохранить
          </Button>
        </>
      }
    >
      <Field label="Название" hint="Пусто — папку назовут по серверам.">
        <Input
          value={name}
          maxLength={32}
          autoFocus
          onChange={(event) => setName(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") done();
          }}
        />
      </Field>
    </Dialog>
  );
}
